import {useCallback, useMemo, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import {BackendApi} from "../api/api.ts";
import {useNotifications} from "../lib/NotificationContext.tsx";
import {documentConflictMessage} from "../lib/apiErrors.ts";
import type {DocumentDrawerActions} from "../views/DocumentDrawer/DocumentDrawerProvider.tsx";
import {BILL_DUPLICATES_QUERY, useBillDuplicates} from "./useBillDuplicates.ts";
import {BillDuplicate, remainingAfterDelete} from "../components/specific/PossibleDuplicates/possibleDuplicates.ts";

type PossibleDuplicatesOptions = {
    openDocument: DocumentDrawerActions['open'];
    onDeleted: (bill: BillDuplicate) => Promise<void> | void;
    closeOnView?: boolean;
};

export function usePossibleDuplicates(api: BackendApi, {openDocument, onDeleted, closeOnView = false}: PossibleDuplicatesOptions) {
    const queryClient = useQueryClient();
    const {dispatch: notifyDispatch} = useNotifications();
    const [billId, setBillId] = useState<string | null>(null);
    const billDuplicatesQuery = useBillDuplicates(api, billId);

    const close = useCallback(() => setBillId(null), []);

    const view = useCallback((bill: BillDuplicate) => {
        const groupBills = billDuplicatesQuery.data?.bills ?? [bill];
        if (closeOnView) {
            setBillId(null);
        }
        openDocument(bill.business_id, bill.document_id, {
            documentList: groupBills.map((groupBill) => ({
                id: groupBill.document_id,
                businessId: groupBill.business_id,
                fileName: groupBill.file_name,
            })),
        });
    }, [billDuplicatesQuery.data, closeOnView, openDocument]);

    const remove = useCallback(async (bill: BillDuplicate) => {
        try {
            await api.Partner__client__unmatchAndDeleteBill({bill_id: bill.bill_id});
            const group = billDuplicatesQuery.data;
            const remaining = group ? remainingAfterDelete(group, bill.bill_id) : [];
            if (remaining.length < 2) {
                setBillId((currentBillId) => currentBillId === billId ? null : currentBillId);
            } else if (bill.bill_id === billId) {
                setBillId((currentBillId) => currentBillId === billId ? remaining[0].bill_id : currentBillId);
            } else {
                await queryClient.invalidateQueries({queryKey: [BILL_DUPLICATES_QUERY]});
            }
            await onDeleted(bill);
            notifyDispatch({
                title: 'Document verwijderd',
                description: bill.is_matched
                    ? 'Het document is ontkoppeld en verwijderd'
                    : 'Het document is succesvol verwijderd',
                type: 'success',
            });
        } catch (error) {
            Sentry.captureException(error);
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                alert(conflictMessage);
            } else {
                notifyDispatch({
                    title: 'Fout bij verwijderen',
                    description: 'Er is een fout opgetreden bij het verwijderen van het document. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
        }
    }, [api, billDuplicatesQuery.data, billId, queryClient, onDeleted, notifyDispatch]);

    const group = billDuplicatesQuery.data ?? null;
    const isLoading = billDuplicatesQuery.isFetching && !billDuplicatesQuery.data;
    const isError = billDuplicatesQuery.isError;
    return useMemo(() => ({
        open: setBillId,
        close,
        modalProps: {
            open: billId !== null,
            onClose: close,
            group,
            isLoading,
            isError,
            onView: view,
            onDelete: remove,
        },
    }), [billId, close, group, isLoading, isError, view, remove]);
}
